import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/seo/Seo";
import Reveal from "@/components/ui-primitives/Reveal";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import { articles, media, CANONICAL_ORIGIN, executive } from "@/data/site";

const filters = ["All", "Article", "Interview", "Podcast", "Press Release", "News", "Speaking Engagement"];

const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export default function Media() {
  const [f, setF] = useState("All");

  const combined = useMemo(() => {
    const asArticles = articles.map((a) => ({
      slug: a.slug,
      type: "Article",
      outlet: "Investors Pathway",
      title: a.title,
      date: a.date,
      location: "Digital",
      image: a.image,
      excerpt: a.dek,
      to: `/leadership/thought-leadership/${a.slug}`,
    }));
    const asMedia = media.map((m) => ({
      ...m,
      to: `/leadership/media/${m.slug}`,
    }));
    return [...asMedia, ...asArticles].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, []);

  const filtered = f === "All" ? combined : combined.filter((c) => c.type === f);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    url: `${CANONICAL_ORIGIN}/leadership/media`,
    name: "Media Center — Adeshi Moffi",
    about: { "@id": `${CANONICAL_ORIGIN}/leadership#adeshi-moffi` },
    hasPart: combined.slice(0, 12).map((c) => ({
      "@type": c.type === "Article" ? "Article" : "CreativeWork",
      name: c.title,
      datePublished: c.date,
      url: `${CANONICAL_ORIGIN}${c.to}`,
      publisher: { "@type": "Organization", name: c.outlet },
    })),
  };

  return (
    <>
      <Seo
        title="Media Center — Adeshi Moffi | Investors Pathway"
        description="Articles, interviews, podcasts, press releases, news, and speaking engagements featuring Adeshi Moffi, President of Investors Pathway LLC."
        path="/leadership/media"
        image={executive.headshot}
        jsonLd={jsonLd}
      />
      <section className="pt-28 md:pt-36 pb-12">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Leadership", to: "/leadership" }, { label: "Media" }]} />
          <Reveal>
            <div className="mt-10 section-num">05 / Media Center</div>
            <h1 data-testid="media-title" className="h-display text-[3rem] md:text-[4.6rem] text-[#0A1128] mt-6 max-w-4xl">
              Coverage, appearances, and public commentary.
            </h1>
            <p className="mt-6 body-lead max-w-3xl">
              A curated record of interviews, panels, press, and published perspectives.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="border-y border-[#E2E8F0] py-4 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="media-filters">
            {filters.map((label) => (
              <button
                type="button"
                key={label}
                onClick={() => setF(label)}
                data-testid={`media-filter-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-xs uppercase tracking-[0.2em] font-sans transition-colors duration-200 ${
                  f === label ? "text-[#0A1128] font-semibold" : "text-[#475569] hover:text-[#0A1128]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="media-grid">
            {filtered.map((c) => (
              <Reveal key={`${c.type}-${c.slug}`}>
                <Link
                  to={c.to}
                  data-testid={`media-card-${c.slug}`}
                  className="group block border border-[#E2E8F0] bg-white hover:border-[#B89947] transition-colors duration-300"
                >
                  <div className="overflow-hidden">
                    <img
                      src={c.image}
                      alt={c.title}
                      loading="lazy"
                      className="w-full h-56 object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-baseline justify-between">
                      <span className="eyebrow text-[#8E7331]">{c.type}</span>
                      <span className="text-[10px] uppercase tracking-[0.16em] text-[#475569] font-sans">{fmt(c.date)}</span>
                    </div>
                    <h3 className="h-editorial text-xl md:text-2xl text-[#0A1128] mt-4 leading-snug">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-sm text-[#475569] font-sans leading-relaxed line-clamp-3">
                      {c.excerpt}
                    </p>
                    <div className="mt-6 pt-4 border-t border-[#E2E8F0] flex justify-between text-xs uppercase tracking-[0.16em] text-[#475569] font-sans">
                      <span>{c.outlet}</span>
                      <span>{c.location}</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="mt-12 body-lead text-[#475569]" data-testid="media-empty">
              No items in this category yet.
            </p>
          )}
        </div>
      </section>
    </>
  );
}
